// A dialog over the screen, for short things that keep the context (create,
// confirm, a change with a large effect).
//
// Promise: opening moves the focus into the dialog; Tab stays inside; Escape
// and the backdrop close it (unless `dismissable` is false); closing returns
// the focus to where it was. It is drawn on the body, over the whole window,
// wherever it was opened from: inside the drawer (the sidebar over the panel)
// the drawer's clip-path and transform kept it to the drawer's box, so a
// dialog opened from a sidebar row was cut away (2026-10-31).
//
// With an on-screen keyboard up (a field inside had the focus), closing goes
// in the opposite order of opening: the keyboard goes down first, then the
// dialog leaves (it stays, out of reach, at most KEYBOARD_CLOSE_WAIT_MS).
// Taking both away at once left iOS's blur at the top of the screen over the
// header in the home screen app (2026-10-02; flashing a colored surface at
// the top afterwards, and shrinking the backdrop by the keyboard's measured
// place, did not help). The focus is not given back to a field on a touch
// device: that brought the keyboard up again.
//
// Inside a host that draws its own parts around the screens (the desktop
// app's rail and sidebar beside the cloud's page), the backdrop goes over
// those too and pressing there closes it (`useOutsideCover`).
import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOutsideCover } from "../shell/outside/cover";
import { useStowHostPartsWhile } from "../shell/phone";
import { afterKeyboardDown, isTextEntry, keyboardUp } from "./soft-keyboard";

/**
 * Short things (the default), the same at a document's width (a preview of a
 * page), or a large panel that lays out its own inside.
 */
/** The backdrop's black (`bg-black/30`), also told to the host around the screens. */
const BACKDROP_SHADE = 0.3;

const SIZES = {
  normal: "w-[min(480px,calc(100vw-32px))] p-5",
  wide: "w-[min(760px,calc(100vw-32px))] p-5",
  large:
    "flex h-[min(86vh,900px)] w-[min(1120px,calc(100vw-32px))] flex-col overflow-hidden",
  /** The whole screen (a phone): no frame, no corners. */
  full: "flex h-full w-full flex-col overflow-hidden",
} as const;

export function Modal({
  open,
  onClose,
  label,
  dismissable = true,
  size = "normal",
  placement = "top",
  hostBar = false,
  className = "",
  children,
}: {
  open: boolean;
  onClose(): void;
  label: string;
  dismissable?: boolean;
  size?: keyof typeof SIZES;
  /**
   * Where a normal or wide dialog stands: near the top (the default; one that
   * grows downward never moves its top), or in the middle (a small panel of a
   * fixed height, as the old web's detail panels).
   */
  placement?: "top" | "center";
  /**
   * The whole screen in a native app that draws the bar at the top: the bar
   * stays and shows this dialog's own header, which its inside hands with
   * `useShellHeader` and `over` (its way back, its close), instead of drawing
   * one of its own.
   */
  hostBar?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  useOutsideCover(open, BACKDROP_SHADE, dismissable ? onClose : null);
  // A native app's own parts over the page (its Dock, its bar at the top)
  // step aside: the dialog has its own close (or, filling the screen, the
  // app's bar shows its header: `hostBar`). A smaller one too: the app does
  // not dim its parts with the backdrop, and the bar's buttons of a dialog
  // under a confirmation could be pressed.
  useStowHostPartsWhile(open, size === "full" && hostBar);
  // Closed while the keyboard is up: kept until it is down.
  const [wasOpen, setWasOpen] = useState(open);
  const [held, setHeld] = useState(false);
  if (wasOpen !== open) {
    setWasOpen(open);
    setHeld(!open && keyboardUp());
  }
  // frontend-development: not a fetch; waits for the keyboard to go down.
  useEffect(() => {
    if (held) return afterKeyboardDown(() => setHeld(false));
  }, [held]);
  // frontend-development: not a fetch; moves focus in and back out.
  useEffect(() => {
    if (!open) return;
    const before = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>(
      "[autofocus], input, textarea, select, button",
    );
    (first ?? panel.current)?.focus();
    return () => {
      const touch = window.matchMedia?.("(pointer: coarse)").matches ?? false;
      if (touch && isTextEntry(before)) {
        // The dialog's field lets go of the keyboard instead.
        if (isTextEntry(document.activeElement)) document.activeElement.blur();
        return;
      }
      before?.focus?.();
    };
  }, [open]);
  if (!open && !held) return null;
  return createPortal(
    <div
      inert={!open || undefined}
      className={`fixed inset-0 z-40 flex justify-center bg-black/30 ${
        size === "full"
          ? "items-stretch"
          : size === "large" || placement === "center"
            ? "items-center"
            : "items-start pt-[12vh]"
      }`}
      onPointerDown={(e) => {
        if (dismissable && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-modal-panel=""
        tabIndex={-1}
        className={`${SIZES[size]} ${
          size === "full"
            ? ""
            : "rounded-[12px] border border-border shadow-[var(--shadow-popover)]"
        } bg-surface ${className}`}
        onKeyDown={(e) => {
          if (e.key === "Escape" && dismissable) {
            e.stopPropagation();
            onClose();
            return;
          }
          if (e.key !== "Tab") return;
          const items = [
            ...(panel.current?.querySelectorAll<HTMLElement>(
              "a[href], button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex='-1'])",
            ) ?? []),
          ];
          if (items.length === 0) return;
          const first = items[0];
          const last = items[items.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
